import { useIntervalComposable } from '@src/application/composables/interval-composable.js';
import { ref } from 'vue';
import { ExpressPickUser } from '@src/express-pick/interfaces/express-pick-user.js';
import apiClient from '@src/api/api-client.js';
import { WarehouseInterface } from '@src/warehouse/interfaces/warehouse-interface.js';
import { closeLoader, openLoader } from '@utils/loader-util.js';
import { asStore } from '@src/application/store/as-store.js';
import { createModal } from '@utils/modal-util.js';
import PickExpressTransferRequests from '@src/express-pick/pick-express-transfer-requests.vue';
import { loadOnBeforeMount } from '@utils/load-util.js';
import { ApiError } from '@src/api/api-error';
import { useCurrentUserComposable } from '@src/user/composable/current-user-composable.ts';

export function useExpressPickComposable() {
    const { createInterval } = useIntervalComposable(() => {
        if (expressPickClusterListOpened.value) {
            return;
        }

        loadExpressPickWarehouses();
    }, 60, 'expressPickWarehouses');

    createInterval();

    const expressPickUser = asStore('expressPickUserStore', () => ref<ExpressPickUser|null>(null));
    const expressPickClusterListOpened = asStore('expressPickClusterListOpened', () => ref(false));
    const expressPickError = asStore('expressPickError', () => ref(false));

    async function loadExpressPickUser() {
        try {
            const request = apiClient.get('/be/express-pick-user/current');
            request.disableErrorModal();
            expressPickUser.value = (await request).data;
        } catch {
            expressPickUser.value = null;
        }

        if (expressPickClusterListOpened.value) {
            return;
        }
        if (!expressPickUser.value?.currentOriginWarehouse) {
            return;
        }
        if (!expressPickUser.value?.currentDestinationBin) {
            return;
        }

        expressPickClusterListOpened.value = true;
        await createModal(false, PickExpressTransferRequests, 'done', null, null, '1');
        await loadExpressPickWarehouses();
        expressPickClusterListOpened.value = false;
    }
    const expressPickWarehouses = asStore('expressPickWarehousesStore', () => ref<Array<WarehouseInterface>>([]));
    async function loadExpressPickWarehouses() {
        if (expressPickUser.value === null) {
            return;
        }

        try {
            const request = apiClient.get('/be/express-pick/warehouses');
            request.disableErrorModal();
            request.setCustomErrorHttpCodes(400);
            expressPickWarehouses.value = (await request).data;
            expressPickError.value = false;
        } catch (e) {
            expressPickWarehouses.value = [];
            if (e instanceof ApiError) {
                expressPickError.value = false;
                return;
            }
            expressPickError.value = true;
        }
    }

    async function assignWarehouse(warehouse: WarehouseInterface) {
        openLoader();
        await apiClient.post('/be/express-pick/assign/' + warehouse.whsCode);
        closeLoader();
        await loadExpressPickUser();
    }

    async function unassignExpressPickQueues() {
        openLoader();
        await apiClient.post('/be/express-pick/unassign');
        expressPickUser.value!.currentOriginWarehouse = null;
        expressPickUser.value!.currentDestinationBin = null;
        closeLoader();
    }

    const { currentUser } = useCurrentUserComposable();
    loadOnBeforeMount(async () => {
        if (!currentUser.value) {
            return;
        }
        await loadExpressPickUser();
        await loadExpressPickWarehouses();
    }, false);

    const toPick = ref<number>(0);

    async function loadToPick() {
        toPick.value = (await apiClient.get('/be/express-pick/to-pick')).data;
    }

    return {
        assignWarehouse,
        unassignExpressPickQueues,
        expressPickUser,
        expressPickWarehouses,
        expressPickError,
        toPick,
        loadToPick,
    };
}
